import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem 1rem;
`;

const LoginCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 400px;
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
    box-shadow: 0 0 0 3px rgba(45, 80, 22, 0.1);
  }

  &.error {
    border-color: #dc3545;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 1.125rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 1.125rem;
  padding: 0.25rem;

  &:hover {
    color: #333;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  background: #2d5016;
  color: white;
  border: none;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #1a3a0e;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }

  span {
    padding: 0 1rem;
    color: #666;
    font-size: 0.875rem;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:hover {
    border-color: #4285f4;
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIyLjU2IDEyLjI1QzIyLjU2IDExLjQ3IDIyLjQ5IDEwLjcyIDIyLjM2IDEwSDEyVjE0LjMzSDE3Ljk2QzE3LjY2IDE1Ljk4IDE2Ljg4IDE3LjM4IDE1Ljc4IDE4LjM5VjIwLjk2SDE5Ljg4QzIxLjUzIDE5LjM0IDIyLjU2IDE3LjA2IDIyLjU2IDEyLjI1WiIgZmlsbD0iIzQyODVGNCIvPgo8cGF0aCBkPSJNMTIgMjNDMTQuOTcgMjMgMTcuNjQgMjEuOTkgMTkuODggMjAuOTZMMTUuNzggMTguMzlDMTQuNzggMTkuMDEgMTMuNTEgMTkuMzMgMTIgMTkuMzNDOS4xNiAxOS4zMyA2Ljg1IDE3LjU3IDYuMDUgMTUuMDdMNC4wOSAxNi41NkM2LjI2IDIwLjg2IDEwLjA2IDIzIDEyIDIzWiIgZmlsbD0iIzM0QTg1MyIvPgo8cGF0aCBkPSJNNi4wNSA5LjA3VjE1LjA3TDEyIDE5LjMzQzE0Ljk3IDE5LjMzIDE3LjY0IDE4LjMyIDE5Ljg4IDE2Ljc5VjIwLjk2SDEyQzEwLjA2IDIzIDYuMjYgMjAuODYgNC4wOSAxNi41Nkw2LjA1IDE1LjA3QzYuODUgMTcuNTcgOS4xNiAxOS4zMyAxMiAxOS4zM1Y5LjA3SDYuMDVaIiBmaWxsPSIjRkJCQzA0Ii8+CjxwYXRoIGQ9Ik0xMiA0LjY3QzEzLjUxIDQuNjcgMTQuNzggNS4wMSAxNS43OCA1LjYzTDE5Ljg4IDMuMDZMMTUuNzggMS4wNEMxMy41MSAwLjAxIDEwLjk3IDAgMTIgMEMxMC4wNiAwIDYuMjYgMi4xNCA0LjA5IDUuNDRMNi4wNSA2LjkzQzYuODUgNC40MyA5LjE2IDIuNjcgMTIgMi42N1Y0LjY3WiIgZmlsbD0iI0VCMzMzMyIvPgo8L3N2Zz4K")
    no-repeat center;
  background-size: contain;
`;

const ForgotPassword = styled(Link)`
  color: #2d5016;
  text-decoration: none;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #2d5016;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  user-select: none;
`;

const SignUpLink = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;

  p {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  a {
    color: #2d5016;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);
  const { login, loginWithGoogle, loading, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const from = location.state?.from?.pathname || "/";

  // Redirect already authenticated users to their dashboard
  React.useEffect(() => {
    if (!loading && isAuthenticated && user && !hasRedirected) {
      setHasRedirected(true);

      let redirectPath = "/";
      if (user.role === "vendor") {
        redirectPath = "/vendor";
      } else if (user.role === "admin" || user.role === "employee") {
        redirectPath = "/admin";
      }

      navigate(redirectPath, { replace: true });
    }
  }, [loading, isAuthenticated, user, hasRedirected, navigate]);

  const onSubmit = async (data) => {
    const result = await login(data, rememberMe);

    if (result.success) {
      // Redirect based on user role
      const { user } = result;
      let redirectPath;

      // If there's a "from" path and it's not the login page itself, use it
      // Otherwise, redirect to role-specific dashboard
      if (from && from !== "/login" && from !== "/") {
        redirectPath = from;
      } else {
        // Default redirect based on role
        if (user?.role === "vendor") {
          redirectPath = "/vendor";
        } else if (user?.role === "admin" || user?.role === "employee") {
          redirectPath = "/admin";
        } else {
          redirectPath = "/";
        }
      }

      // Use setTimeout to ensure state is fully updated before navigation
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 100);
    } else {
      setError("root", {
        type: "manual",
        message: result.error,
      });
    }
  };

  // Don't render form if already authenticated (prevents flash)
  if (!loading && isAuthenticated && user) {
    return null;
  }

  return (
    <LoginContainer>
      <LoginCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <LoginHeader>
          <h1>Welcome Back</h1>
          <p>Sign in to your NourishGY account</p>
        </LoginHeader>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <InputContainer>
              <InputIcon>
                <FiMail />
              </InputIcon>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                className={errors.email ? "error" : ""}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
              />
            </InputContainer>
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <InputContainer>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className={errors.password ? "error" : ""}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputContainer>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>

          <RememberMeContainer>
            <Checkbox
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <CheckboxLabel htmlFor="rememberMe">
              Remember me for 30 days
            </CheckboxLabel>
          </RememberMeContainer>

          {errors.root && <ErrorMessage>{errors.root.message}</ErrorMessage>}

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner" />
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </SubmitButton>

          <Divider>
            <span>or</span>
          </Divider>

          <GoogleButton type="button" onClick={loginWithGoogle}>
            <GoogleIcon />
            Continue with Google
          </GoogleButton>

          <ForgotPassword to="/forgot-password">
            Forgot your password?
          </ForgotPassword>
        </Form>

        <SignUpLink>
          <p>Don't have an account?</p>
          <Link to="/register">Sign up for NourishGY</Link>
        </SignUpLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
